import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { motion } from 'framer-motion';
import L from 'leaflet';
import {
  MapPin, Search, Filter, Building2, Store, Warehouse, Home,
  Eye, Edit, XCircle, AlertCircle, CheckCircle, Clock
} from 'lucide-react';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom marker icons based on property type and status
const createCustomIcon = (type, status) => {
  const getColor = () => {
    if (status === 'occupied') return '#10B981'; // green
    if (status === 'vacant') return '#6B7280'; // gray
    if (status === 'maintenance') return '#F59E0B'; // orange
    return '#EF4444'; // red
  };

  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="background-color: ${getColor()}; width: 30px; height: 30px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center;">
      <span style="color: white; font-size: 12px; font-weight: bold;">${type === 'store' ? 'S' : type === 'warehouse' ? 'W' : type === 'building' ? 'B' : 'O'}</span>
    </div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 30],
  });
};

const Map = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [mapCenter, setMapCenter] = useState([24.7136, 46.6753]); // Riyadh center

  // Mock properties with coordinates (matching the prototypes)
  const properties = [
    {
      id: 'PROP-001',
      internalId: 'LOC-RYD-001',
      name: 'Riyadh Main Store',
      type: 'store',
      address: 'King Fahd Road, Riyadh',
      city: 'Riyadh',
      lat: 24.729013,
      lng: 46.642046,
      area: 500,
      status: 'occupied',
      monthlyRent: 15000,
      currentTenant: 'ABC Retail Company',
      tenantContact: '+966 50 123 4567',
      occupancyRate: 100,
      issuesCount: 0,
    },
    {
      id: 'PROP-002',
      internalId: 'LOC-JED-001',
      name: 'Jeddah Warehouse Complex',
      type: 'warehouse',
      address: 'Industrial Area, Jeddah',
      city: 'Jeddah',
      lat: 21.4858,
      lng: 39.1925,
      area: 2000,
      status: 'occupied',
      monthlyRent: 25000,
      currentTenant: 'XYZ Logistics',
      tenantContact: '+966 50 987 6543',
      occupancyRate: 100,
      issuesCount: 2,
    },
    {
      id: 'PROP-003',
      internalId: 'LOC-DMM-001',
      name: 'Dammam Office Building',
      type: 'building',
      address: 'Corniche Road, Dammam',
      city: 'Dammam',
      lat: 26.4207,
      lng: 50.0888,
      area: 1200,
      status: 'occupied',
      monthlyRent: 18000,
      currentTenant: 'Tech Solutions Inc.',
      tenantContact: '+966 50 555 1234',
      occupancyRate: 100,
      issuesCount: 1,
    },
    {
      id: 'PROP-004',
      internalId: 'LOC-RYD-002',
      name: 'Riyadh Retail Space',
      type: 'store',
      address: 'Olaya Street, Riyadh',
      city: 'Riyadh',
      lat: 24.719000,
      lng: 46.612046,
      area: 300,
      status: 'vacant',
      monthlyRent: 12000,
      currentTenant: null,
      tenantContact: null,
      occupancyRate: 0,
      issuesCount: 0,
    },
    {
      id: 'PROP-005',
      internalId: 'LOC-KBU-001',
      name: 'Khobar Storage Facility',
      type: 'warehouse',
      address: 'Industrial Zone, Khobar',
      city: 'Khobar',
      lat: 26.2172,
      lng: 50.1971,
      area: 1500,
      status: 'maintenance',
      monthlyRent: 20000,
      currentTenant: 'Storage Co.',
      tenantContact: '+966 50 444 5678',
      occupancyRate: 75,
      issuesCount: 3,
    },
  ];

  const filteredProperties = properties.filter(prop => {
    const matchesSearch = prop.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      prop.internalId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      prop.address.toLowerCase().includes(searchQuery.toLowerCase()) ||
      prop.city.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = selectedFilter === 'all' || prop.status === selectedFilter;
    
    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'occupied': return 'bg-green-100 text-green-700';
      case 'vacant': return 'bg-gray-100 text-gray-700';
      case 'maintenance': return 'bg-orange-100 text-orange-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'store': return Store;
      case 'warehouse': return Warehouse;
      case 'building': return Building2;
      default: return Home;
    }
  };

  const handleMarkerClick = (property) => {
    setSelectedProperty(property);
    setMapCenter([property.lat, property.lng]);
  };

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Property Map</h2>
            <p className="text-sm text-gray-600 mt-1">
              View all properties on an interactive map
            </p>
          </div>
          <div className="flex gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search properties..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <select
              value={selectedFilter}
              onChange={(e) => setSelectedFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="all">All Status</option>
              <option value="occupied">Occupied</option>
              <option value="vacant">Vacant</option>
              <option value="maintenance">Maintenance</option>
            </select>
          </div>
        </div>
      </div>

      {/* Map and Property List */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Map */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="h-[600px] rounded-lg overflow-hidden">
              <MapContainer
                center={mapCenter}
                zoom={6}
                style={{ height: '100%', width: '100%' }}
                zoomControl={true}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                {filteredProperties.map((property) => (
                  <Marker
                    key={property.id}
                    position={[property.lat, property.lng]}
                    icon={createCustomIcon(property.type, property.status)}
                    eventHandlers={{
                      click: () => handleMarkerClick(property),
                    }}
                  >
                    <Popup>
                      <div className="p-2 min-w-[200px]">
                        <h3 className="font-semibold text-gray-900 mb-2">{property.name}</h3>
                        <p className="text-sm text-gray-600 mb-1">{property.internalId}</p>
                        <p className="text-sm text-gray-600 mb-2">{property.address}</p>
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(property.status)}`}>
                            {property.status}
                          </span>
                          <span className="text-xs text-gray-600 capitalize">{property.type}</span>
                        </div>
                        {property.currentTenant && (
                          <p className="text-sm text-gray-700 mb-1">
                            <strong>Tenant:</strong> {property.currentTenant}
                          </p>
                        )}
                        <p className="text-sm text-gray-700 mb-2">
                          <strong>Rent:</strong> SAR {property.monthlyRent.toLocaleString()}/month
                        </p>
                        <button
                          onClick={() => handleMarkerClick(property)}
                          className="w-full mt-2 px-3 py-1 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium"
                        >
                          View Details
                        </button>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          </div>
        </div>

        {/* Property List */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Properties ({filteredProperties.length})</h3>
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {filteredProperties.map((property) => {
                const TypeIcon = getTypeIcon(property.type);
                return (
                  <motion.div
                    key={property.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedProperty?.id === property.id
                        ? 'border-amber-600 bg-amber-50'
                        : 'border-gray-200 hover:border-amber-300 hover:bg-gray-50'
                    }`}
                    onClick={() => handleMarkerClick(property)}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${
                        property.status === 'occupied' ? 'bg-green-100' :
                        property.status === 'vacant' ? 'bg-gray-100' :
                        'bg-orange-100'
                      }`}>
                        <TypeIcon className={`w-5 h-5 ${
                          property.status === 'occupied' ? 'text-green-600' :
                          property.status === 'vacant' ? 'text-gray-600' :
                          'text-orange-600'
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-gray-900 truncate">{property.name}</h4>
                        <p className="text-xs text-gray-600 mb-1">{property.internalId}</p>
                        <p className="text-xs text-gray-600 mb-2">{property.city}</p>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(property.status)}`}>
                            {property.status}
                          </span>
                          {property.issuesCount > 0 && (
                            <span className="flex items-center gap-1 text-xs text-orange-600">
                              <AlertCircle className="w-3 h-3" />
                              {property.issuesCount} issue{property.issuesCount > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                        <p className="text-sm font-semibold text-gray-900 mt-2">
                          SAR {property.monthlyRent.toLocaleString()}/month
                        </p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Legend</h3>
        <div className="flex flex-wrap gap-6">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-green-500 border-2 border-white shadow"></div>
            <span className="text-sm text-gray-700">Occupied</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-gray-500 border-2 border-white shadow"></div>
            <span className="text-sm text-gray-700">Vacant</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-orange-500 border-2 border-white shadow"></div>
            <span className="text-sm text-gray-700">Maintenance</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">S = Store, W = Warehouse, B = Building, O = Office</span>
          </div>
        </div>
      </div>

      {/* Property Detail Modal */}
      {selectedProperty && (
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">Property Details</h3>
              <button
                onClick={() => setSelectedProperty(null)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <XCircle className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Property Name</label>
                  <p className="text-lg text-gray-900">{selectedProperty.name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Internal ID</label>
                  <p className="text-lg text-gray-900">{selectedProperty.internalId}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Type</label>
                  <p className="text-lg text-gray-900 capitalize">{selectedProperty.type}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Status</label>
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedProperty.status)}`}>
                    {selectedProperty.status}
                  </span>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Address</label>
                  <p className="text-lg text-gray-900">{selectedProperty.address}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">City</label>
                  <p className="text-lg text-gray-900">{selectedProperty.city}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Area</label>
                  <p className="text-lg text-gray-900">{selectedProperty.area} sqm</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Monthly Rent</label>
                  <p className="text-lg font-semibold text-gray-900">SAR {selectedProperty.monthlyRent.toLocaleString()}</p>
                </div>
                {selectedProperty.currentTenant && (
                  <>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Tenant</label>
                      <p className="text-lg text-gray-900">{selectedProperty.currentTenant}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Contact</label>
                      <p className="text-lg text-gray-900">{selectedProperty.tenantContact}</p>
                    </div>
                  </>
                )}
              </div>
              <div className="pt-4 border-t border-gray-200 flex gap-3">
                <button className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  View Full Details
                </button>
                <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2">
                  <Edit className="w-4 h-4" />
                  Edit Property
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Map;
